import { AllSelection, TextSelection } from '@atlaskit/editor-prosemirror/state';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { createCommand } from './decoration-plugin-factory';
import { addAnalytics } from '../../analytics/utils';
import { isConfluenceTitleToolbarSuggestTitle } from '../../utils/confluence-suggest-title/is-title-toolbar-suggest-title';
import { analyticsFormatMessage } from '../../utils/format-message';
export const createWidgetDecoration = (pos, type, key) => {
  const widgetSpanElement = document.createElement('span');
  // Firefox needs content inside the span in order to add height to the element.
  // Otherwise, the height is set to 0 which prevents the start and
  // end widgets from rendering correctly and produces incorrect measurements
  // when we need to determine where to place the modal under the content.
  // Zero width spaces have been known to introduce some strange selection
  // behaviour in the Editor, however this approach works for our use case
  // in the ai-plugin where we do not expect users to be making selections
  // over ai-highlighted content in the same way they select regular content
  // in the Editor, this is also not intended to be a permanent fix post EAP.
  widgetSpanElement.appendChild(document.createTextNode('\u200b'));
  widgetSpanElement.setAttribute('data-testid', `ai-modal-${type}-widget`);
  // TODO: https://product-fabric.atlassian.net/browse/ED-17641
  // Issue in safari when pressing cmd+shift+right at start widget highlights
  // all text
  widgetSpanElement.className = `ai-modal-${type}-widget`;
  widgetSpanElement.contentEditable = 'false';
  return Decoration.widget(pos, widgetSpanElement, {
    side: type === 'start' ? 0 : -1,
    key: key !== null && key !== void 0 ? key : `${type}WidgetDecoration`
  });
};
export const createInlineDecoration = (from, to) => {
  return Decoration.inline(from, to, {
    class: 'ai-selection-highlight',
    'data-testid': 'ai-selection-highlight'
  }, {
    key: 'inlineDecoration'
  });
};
export const createGeneratedContentDecorations = (from, to) => {
  const startWidgetDecoration = createWidgetDecoration(from, 'start', 'generatedStartWidget');
  const highlightInlineDecoration = Decoration.inline(from, to, {
    class: 'ai-generated-content-highlight',
    'data-testid': 'ai-generated-content-highlight'
  }, {
    key: 'generatedInlineDecoration'
  });
  const endWidgetDecoration = createWidgetDecoration(to, 'end', 'generatedEndWidget');
  return [startWidgetDecoration, highlightInlineDecoration, endWidgetDecoration];
};
function getInitialPositions(selection) {
  switch (true) {
    case selection instanceof AllSelection:
      return {
        startPos: selection.from + 1,
        endPos: selection.to - 1
      };
    default:
      return {
        startPos: selection.from,
        endPos: selection.to
      };
  }
}
export const createOpenAIModalCommand = ({
  state,
  configItem,
  lastTriggeredFrom,
  triggerMethod,
  aiGlobalOptIn
}) => {
  if (aiGlobalOptIn.status === 'disabled') {
    // If the user has opted out of AI, do not show the modal
    // it's unclear how this function would be triggered if the
    // ai feature is disabled as all entry points should be removed
    return () => false;
  }
  if (aiGlobalOptIn.status === 'disabled-opt-in') {
    aiGlobalOptIn.triggerOptInFlow();
    return createCommand({}, tr => {
      addAnalytics({
        editorState: state,
        tr,
        payload: {
          action: 'triggerOptInFlow',
          actionSubject: 'editorPluginAI',
          actionSubjectId: 'aiGlobalOptIn',
          attributes: {
            experienceType: configItem.title({
              formatMessage: analyticsFormatMessage
            }),
            lastTriggeredFrom,
            selectionType: configItem.selectionType
          },
          eventType: 'ui'
        }
      });
      return tr;
    });
  }

  /**
   * We add decoration based on the current selection
   * state of the document and the config item in the command palette
   * 2 scenarios
   * 1. If the selection is empty and the document is not empty -> we decorate the entire document
   * 2. If the selection is empty and the current config type is 'empty config' -> we decorate based on selection preview plugin
   */
  let useCurrentSelection = true;
  if (state.selection.empty) {
    useCurrentSelection = state.doc.content.size !== 0 && configItem.selectionType !== 'empty' ? false : true;
  }

  /**
   * EXPERIMENTAL: For experimental flow where 'Suggest title' in
   * Confluence title toolbar will trigger the 'Suggest a title' flow in Editor AI.
   * Forces whole document selection, even if an existing selection exists.
   */
  if (isConfluenceTitleToolbarSuggestTitle({
    configItem,
    lastTriggeredFrom
  })) {
    useCurrentSelection = false;
  }

  // First get the positions that the decorations will be added to the document at
  const {
    startPos,
    endPos
  } = getInitialPositions(useCurrentSelection ? state.selection : new AllSelection(state.doc));

  // Then create the decorations and add them to a decoration set
  const startWidgetDecoration = createWidgetDecoration(startPos, 'start');
  const endWidgetDecoration = createWidgetDecoration(endPos, 'end');
  const highlightInlineDecoration = createInlineDecoration(startPos, endPos);
  const widgetDecorations = startPos === endPos ? [endWidgetDecoration] : [startWidgetDecoration, endWidgetDecoration];
  const modalDecorationSet = DecorationSet.create(state.doc, [...widgetDecorations, highlightInlineDecoration]);

  // This "command" is later picked up by a state reducer
  const action = {
    type: 'SHOW_MODAL',
    data: {
      lastTriggeredFrom,
      triggerMethod,
      modalDecorationSet,
      configItem,
      modalMountedTimeStamp: Date.now()
    }
  };

  // Clear the selection after opening the modal to hide annotation toolbar
  return createCommand(action, tr => {
    const {
      to,
      from
    } = tr.selection;
    if (to !== from) {
      tr.setSelection(TextSelection.create(tr.doc, to));
    }
    return tr;
  });
};
export const createEndAIExperienceCommand = modalDecorationSet => {
  const action = {
    type: 'END_EXPERIENCE',
    data: modalDecorationSet ? {
      modalDecorationSet
    } : {}
  };
  return createCommand(action);
};